import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { apiGet, apiPath, apiPut } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';

import type { AlmParameters, BucketConfig, CurveAssignment } from './globalTypes';

const KEY         = ['alm', 'parameters'] as const;
const BUCKETS_KEY = ['alm', 'parameters', 'buckets'] as const;

/**
 * GET /api/v1/alm/parameters — read the tenant's global parameters row.
 * Any ALM role can read; only ALM_ADMIN can write.
 */
export function useAlmParameters(
  options?: Omit<UseQueryOptions<AlmParameters, CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: KEY,
    queryFn: () => apiGet<AlmParameters>(apiPath('alm', 'parameters')),
    ...options,
  });
}

/** PUT /api/v1/alm/parameters — replace the whole parameters row. */
export function useUpdateAlmParameters(
  options?: UseMutationOptions<AlmParameters, CubixApiError, AlmParameters>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiPut<AlmParameters, AlmParameters>(apiPath('alm', 'parameters'), body),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: KEY });
      return options?.onSuccess?.(...args);
    },
  });
}

// ── Bucket config (Phase 3.5) ─────────────────────────────────────────────────

/**
 * GET /api/v1/alm/parameters/buckets — returns the active bucket grid.
 * Falls back to 7 Basel defaults when no custom config is saved
 * (isDefault=true on each row).
 */
export function useAlmBuckets(
  options?: Omit<UseQueryOptions<BucketConfig, CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: BUCKETS_KEY,
    queryFn: () => apiGet<BucketConfig>(apiPath('alm', 'parameters', 'buckets')),
    ...options,
  });
}

/** PUT /api/v1/alm/parameters/buckets — replace the full bucket grid. */
export function useUpdateAlmBuckets(
  options?: UseMutationOptions<BucketConfig, CubixApiError, BucketConfig>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiPut<BucketConfig, BucketConfig>(apiPath('alm', 'parameters', 'buckets'), body),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: BUCKETS_KEY });
      return options?.onSuccess?.(...args);
    },
  });
}

// ── Curve assignments ─────────────────────────────────────────────────────────

/** PUT /api/v1/alm/parameters/curves — replace the per-currency curve map. */
export function useUpdateCurveAssignments(
  options?: UseMutationOptions<CurveAssignment[], CubixApiError, CurveAssignment[]>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiPut<CurveAssignment[], CurveAssignment[]>(apiPath('alm', 'parameters', 'curves'), body),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: KEY });
      return options?.onSuccess?.(...args);
    },
  });
}
